import React from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion"; // Import framer-motion

function Contact() {
  const { t } = useTranslation();

  // Define the animation variants for the Contact Details Section
  const contactDetailsVariants = {
    hidden: { opacity: 0, y: 50 }, // Start 50px below and invisible
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.2, // Stagger the animation of child elements (contact detail cards)
      },
    },
  };

  // Animation variants for individual contact detail cards
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  // Define the animation variants for the Map Section
  const mapVariants = {
    hidden: { opacity: 0, y: 50 }, // Start 50px below and invisible
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    }, // Fade in and slide up
  };

  return (
    <div>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{t("contact.seoTitle")}</title>
        <meta name="description" content={t("contact.seoDescription")} />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center h-[400px] flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/assets/images/contact.jpeg')`,
        }}
      >
        <div className="text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t("contact.heroTitle")}
          </h1>
          <p className="text-lg md:text-xl">{t("contact.heroSubtitle")}</p>
        </div>
      </section>

      {/* Contact Details Section */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible" // Animate when the element comes into view
            viewport={{ once: true, amount: 0.2 }} // Trigger animation once when 20% of the element is visible
            variants={contactDetailsVariants}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-textPrimary mb-6">
              {t("contact.detailsTitle")}
            </h2>
            <p className="text-lg text-textPrimary mb-8">
              {t("contact.detailsSubtitle")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <motion.div
                variants={cardVariants}
                className="flex flex-col items-center"
              >
                <svg
                  className="w-8 h-8 text-accent mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <p className="text-textPrimary font-semibold">
                  {t("contact.addressLabel")}
                </p>
                <p className="text-textPrimary">{t("contact.address")}</p>
              </motion.div>
              <motion.div
                variants={cardVariants}
                className="flex flex-col items-center"
              >
                <svg
                  className="w-8 h-8 text-accent mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-textPrimary font-semibold">
                  {t("contact.emailLabel")}
                </p>
                <a
                  href="mailto:info@notashamedofthegospelministry.org"
                  className="text-primary hover:underline"
                >
                  {t("contact.email")}
                </a>
              </motion.div>
              <motion.div
                variants={cardVariants}
                className="flex flex-col items-center"
              >
                <svg
                  className="w-8 h-8 text-accent mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <p className="text-textPrimary font-semibold">
                  {t("contact.phoneLabel")}
                </p>
                <a
                  href="tel:+243-857-274-262"
                  className="text-primary hover:underline"
                >
                  {t("contact.phone")}
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible" // Animate when the element comes into view
            viewport={{ once: true, amount: 0.2 }} // Trigger animation once when 20% of the element is visible
            variants={mapVariants}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-textPrimary text-center mb-6">
              {t("contact.mapTitle")}
            </h2>
            <div className="max-w-4xl mx-auto">
              <iframe
                title="Goma North Kivu Map"
                src="https://www.google.com/maps?q=Goma,+North+Kivu,+DRC&output=embed"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
